import { EventCode, LogData, Snippet } from 'ranna-ts';
import { NotificationType, SnackBar } from 'components/SnackBar';
import { useEffect, useRef, useState } from 'react';

import { Editor } from 'components/Editor';
import { Header } from 'components/Header';
import { Result } from 'types/restapi';
import { ResultViewer } from 'components/ResultViewer';
import { SettingsModal } from 'components/SettingsModal';
import { SnippetNotification } from './SnippetNotification';
import { Snippets } from 'services/static';
import { WSInfoModal } from 'components/WSInfoModal';
import styled from 'styled-components';
import { useCodeExec } from 'hooks/useCodeExec';
import { useInfo } from 'hooks/useInfo';
import { useInitAPIClient } from 'hooks/useInitAPIClient';
import { useIsEmbedded } from 'hooks/useIsEmbedded';
import { useQuery } from 'hooks/useQuery';
import { useShallow } from 'zustand/react/shallow';
import { useSnackBar } from 'components/SnackBar/useSnackBar';
import { useSpec } from 'hooks/useSpec';
import { useStore } from 'services/store';

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const EditorContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
`;

const EmbedFooter = styled.div`
  background-color: ${(p) => p.theme.accentDark};
  font-size: 0.7rem;
  padding: 0.2em 0.4em;
  display: flex;
  justify-content: space-between;

  a {
    color: ${(p) => p.theme.text};
  }
`;

export const MainRoute: React.FC = () => {
  useInitAPIClient();

  const specMap = useSpec();
  const [code, setCode, spec, setSpec, apiKey, useWS] = useStore(
    useShallow((s) => [s.code, s.setCode, s.spec, s.setSpec, s.apiKey, s.useWS])
  );

  const [snippet, setSnippet] = useQuery('s');
  const { show } = useSnackBar();
  const { run, stop } = useCodeExec();
  const systemInfo = useInfo();
  const isEmbedded = useIsEmbedded();

  const lastSnippetRef = useRef<string>('');
  const dirty = useRef<boolean>(false);
  const [isRunning, setIsRunning] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [stdOut, setStdOut] = useState('');
  const [stdErr, setStdErr] = useState('');
  const [result, setResult] = useState<Result>();

  useEffect(() => {
    if (snippet) {
      Snippets.get(snippet)
        .then((s) => {
          setCode(s.code);
          lastSnippetRef.current = s.code;
          dirty.current = true;
          setSpec(s.language);
        })
        .catch((e) => {
          if (e.code === 404)
            show(
              'No snippet with this ID was found or it has been removed.',
              NotificationType.ERROR,
              6000
            );
          else {
            show(
              <span>
                An unexpected error occured on loading the snippet.
                <br />
                Please try again later.
              </span>,
              NotificationType.ERROR,
              6000
            );
            console.error(e);
          }
        });
    }
  }, []);

  useEffect(() => {
    if (!spec && specMap) {
      const s = Object.keys(specMap)[0];
      setSpec(s);
    }

    if (
      !dirty.current &&
      !snippet &&
      specMap &&
      spec &&
      specMap[spec].example
    ) {
      setCode(specMap[spec].example);
    }
  }, [spec, specMap]);

  const _run = () => {
    _reset();
    run().subscribe({
      error() {
        setIsRunning(false);
      },
      complete() {
        setIsRunning(false);
      },
      next(e) {
        switch (e?.code) {
          case EventCode.SPAWN:
            setIsRunning(true);
            break;
          case EventCode.LOG:
            {
              const data = e.data as LogData;
              if (data.stdout) setStdOut((v) => v + data.stdout);
              if (data.stderr) setStdErr((v) => v + data.stderr);
            }
            break;
          case EventCode.STOP:
            setResult(e.data as Result);
            break;
        }
      },
    });
  };

  const _reset = () => {
    setStdOut('');
    setStdErr('');
    setResult(undefined);
  };

  const _postSnippet = async () => {
    if (snippet && code === lastSnippetRef.current) {
      show(<SnippetNotification ident={snippet} />, NotificationType.INFO, 0);
      return;
    }
    Snippets.clientOptions.auth = apiKey ? `bearer ${apiKey}` : '';
    try {
      const res = await Snippets.create({
        code,
        language: spec,
      } as Snippet);
      setSnippet(res.ident);
      lastSnippetRef.current = code;
      show(<SnippetNotification ident={res.ident} />, NotificationType.INFO, 0);
    } catch {}
  };

  const _onEditorChange = (v: string) => {
    dirty.current = !!v;
    setCode(v);
  };

  return (
    <Container>
      <Header
        onOpenSettings={() => setSettingsOpen(true)}
        specMap={specMap}
        info={systemInfo}
        isActive={isRunning}
        onExec={_run}
        onStop={useWS ? stop : undefined}
        onSnippet={_postSnippet}
        isEmbedded={isEmbedded}
      />
      {settingsOpen && (
        <SettingsModal onClosing={() => setSettingsOpen(false)} />
      )}
      <EditorContainer>
        <Editor
          value={code}
          onChange={_onEditorChange}
          selectedLang={(specMap ?? {})[spec]?.language ?? spec}
          readOnly={isEmbedded}
        />
        <ResultViewer
          isRunning={isRunning}
          stdOut={stdOut}
          stdErr={stdErr}
          result={result}
          onClosing={_reset}
        />
      </EditorContainer>
      {isEmbedded && (
        <EmbedFooter>
          <a target="_blank" href={window.location.href} rel="noreferrer">
            Provided with ❤️ by ranna.
          </a>
          <span>
            Spec: <code>{spec}</code>
          </span>
        </EmbedFooter>
      )}
      <SnackBar />
      <WSInfoModal />
    </Container>
  );
};
